import {
  Box,
  IconButton,
  InputBase,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import GroupsIcon from '@mui/icons-material/Groups';
import { API_POST, DBRequest, useConfig } from '../../../../api/api';
import useFetch from '../../../../api/useFetch';
import { tokens } from '../../../../global/theme/tokens';
import usePersistedState from '../../../../global/usePersistedState';
import Pagination from '../../../special/Pagination';
import ProjectsMultiSelect from '../../../select/ProjectsMultiSelect';
import EmployeesMultiSelect from '../../../select/EmployeesMultiSelect';
import SortSelect from '../../../select/SortSelect';
import { ExoSkeleton } from '../../ExoSkeleton';
import TaskCardList from '../task-card/TaskCardList';
import PoolTaskCard from './PoolTaskCard';
import {
  buildPoolQuery,
  emptyPoolFilter,
  POOL_PER_PAGE,
  POOL_TASKS_PATH,
  withQueryOnLinks,
} from './poolQuery';

// what the pool sorts by, with the direction each field opens in - the API
// default without a field is the creation date
const POOL_SORT_OPTIONS = (t) => [
  { value: 'name', label: t('Name'), defaultOrder: 'asc' },
  { value: 'priority', label: t('Priority'), defaultOrder: 'desc' },
  // the nearest deadline and the biggest tasks are what a pool is scanned for
  { value: 'deadline', label: t('Deadline'), defaultOrder: 'asc' },
  { value: 'effortScore', label: t('Points'), defaultOrder: 'desc' },
  { value: 'createdAt', label: t('Created'), defaultOrder: 'desc' },
];

/**
 * Open tasks to pick up. Every filter is a query param, so the list is always
 * the complete result and never just the part that happens to be loaded.
 *
 * usersProjects: limits the pool to the projects of the logged in user
 * projects: options of the project filter
 * manage: assign anyone to a task instead of taking it over, plus the
 *   employee filter that only makes sense when the pool is not the own one
 * users: options of the employee filter
 * onTaken: called with the task after it was taken over
 * onUsersChange: called with (task, members) after the assign popper changed
 *   who is on a task, so the page next to the pool can follow
 * reloadRef: ref the pool puts its reload into, so the page can refresh it
 *   when a task is freed up outside of the pool
 */
const TaskPool = ({
  usersProjects,
  projects = [],
  manage,
  users = [],
  perPage = POOL_PER_PAGE,
  onTaken,
  onTotalChange,
  onUsersChange,
  onFeedback,
  reloadRef,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const userId = Number(new Cookies().get('userId'));

  // the pool settings survive a reload, only the search starts out empty -
  // the managing pool and the own one each keep their own
  const [storedFilter, setStoredFilter] = usePersistedState(
    `taskPool.${manage ? 'manage' : 'own'}.filter`,
    emptyPoolFilter,
  );
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isPaginating, setIsPaginating] = useState(false);
  const [assigningIds, setAssigningIds] = useState([]);

  const filter = useMemo(
    () => ({ ...storedFilter, search }),
    [storedFilter, search],
  );

  // typing should not fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const query = useMemo(
    () => buildPoolQuery(filter, { usersProjects, userId }),
    [filter, usersProjects, userId],
  );

  const {
    apiData,
    setApiData,
    reloadApiData,
    isLoading: isFetching,
  } = useFetch(`${POOL_TASKS_PATH}?${query}&perPage=${perPage}`);

  // reloadApiData closes over the current query, so the ref is kept fresh
  useEffect(() => {
    if (reloadRef) reloadRef.current = reloadApiData;
  });

  const tasks = apiData?.data || [];
  const total = apiData?.meta?.total ?? tasks.length;

  // a changing callback must not restart the effect
  const reportTotal = useRef(onTotalChange);
  reportTotal.current = onTotalChange;
  useEffect(() => {
    if (reportTotal.current) reportTotal.current(total);
  }, [total]);

  function updateFilter(changes) {
    setStoredFilter((prev) => ({ ...prev, ...changes }));
  }

  // the members dialog reports the new team, an assigned task leaves the free list
  function handleUsersChange(task, members) {
    const leavesPool = filter.withoutUsers && members.length;

    setApiData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        data: leavesPool
          ? prev.data.filter((item) => item.id !== task.id)
          : prev.data.map((item) =>
              item.id === task.id ? { ...item, users: members } : item,
            ),
        meta:
          leavesPool && prev.meta
            ? { ...prev.meta, total: Math.max((prev.meta.total || 1) - 1, 0) }
            : prev.meta,
      };
    });
  }

  function handleTakeOver(task) {
    if (!userId || assigningIds.includes(task.id)) return;

    // the user joins the task, everyone already on it stays
    const relations = [...(task.users || []).map((user) => user.id), userId];

    DBRequest({
      config,
      path: `tasks/${task.id}/members`,
      method: API_POST({
        relations: [...new Set(relations)],
        mode: 'sync',
        relationType: 'users',
      }),
      onLoading: (isAssigning) =>
        setAssigningIds((prev) =>
          isAssigning
            ? [...prev, task.id]
            : prev.filter((id) => id !== task.id),
        ),
      onResponse: (res) => {
        setApiData((prev) =>
          prev
            ? {
                ...prev,
                data: prev.data.filter((item) => item.id !== task.id),
                meta: prev.meta && {
                  ...prev.meta,
                  total: Math.max((prev.meta.total || 1) - 1, 0),
                },
              }
            : prev,
        );
        if (onTaken) onTaken({ ...task, ...res.data });
      },
      onFeedback: (text, type) =>
        onFeedback && onFeedback(text || t('Task assigned to you'), type),
    });
  }

  // the assign popper toggles one employee on or off a task
  function handleToggleUser(task, user) {
    if (assigningIds.includes(task.id)) return;

    const assignees = task.users || [];
    const isAssigned = assignees.some((member) => member.id === user.id);
    const members = isAssigned
      ? assignees.filter((member) => member.id !== user.id)
      : [...assignees, user];

    DBRequest({
      config,
      path: `tasks/${task.id}/members`,
      method: API_POST({
        relations: members.map((member) => member.id),
        mode: 'sync',
        relationType: 'users',
      }),
      onLoading: (isAssigning) =>
        setAssigningIds((prev) =>
          isAssigning
            ? [...prev, task.id]
            : prev.filter((id) => id !== task.id),
        ),
      onResponse: (res) => {
        const updatedMembers = res.data?.users || members;
        handleUsersChange(task, updatedMembers);
        // the page next to the pool shows what the employees have on their
        // plate, so it has to follow an assignment made here
        if (onUsersChange) onUsersChange(task, updatedMembers);
      },
      onFeedback: (text, type) =>
        onFeedback && onFeedback(text || t('Task updated'), type),
    });
  }

  const isLoading = (isFetching && !apiData) || isPaginating;

  const paginationData = useMemo(
    () => withQueryOnLinks(apiData, `${query}&perPage=${perPage}`),
    [apiData, query, perPage],
  );

  return (
    <Box
      className="flex flex-col gap-3 rounded-2xl p-3 w-full min-w-0 lg:min-h-0 lg:overflow-hidden"
      sx={{
        background: `linear-gradient(135deg, ${colors.glassFrom}, ${colors.glassTo})`,
        border: `1px solid ${colors.selected}`,
        backdropFilter: 'blur(12px)',
      }}
    >
      <Box className="flex items-center gap-2 shrink-0">
        <InboxIcon sx={{ fontSize: '18px', color: colors.warning }} />
        <Typography fontSize="16px" fontWeight={600} className="shrink-0">
          {t('Pool')}
        </Typography>
        <Typography fontSize="11px" className="opacity-60 grow min-w-0" noWrap>
          {filter.withoutUsers ? t('Unassigned') : t('All active')}
        </Typography>
        <Box
          className="rounded-lg px-2 py-0.5 shrink-0"
          sx={{ backgroundColor: colors.bgInput }}
        >
          <Typography fontSize="12px" fontWeight={600} className="monospace">
            {total}
          </Typography>
        </Box>
      </Box>

      <Box
        className="flex items-center gap-2 rounded-lg px-2 shrink-0"
        sx={{ backgroundColor: colors.bgInput, backdropFilter: 'blur(10px)' }}
      >
        <SearchIcon sx={{ fontSize: '18px', opacity: 0.5 }} />
        <InputBase
          fullWidth
          value={searchInput}
          placeholder={t('Search task')}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setSearchInput('')}
          sx={{ fontSize: '13px', paddingY: '4px' }}
        />
        {Boolean(searchInput) && (
          <IconButton size="small" onClick={() => setSearchInput('')}>
            <CloseIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        )}
      </Box>

      <Box className="flex flex-wrap items-center gap-2 shrink-0">
        {/* free tasks, or every active one so the user can join it */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter.withoutUsers}
          onChange={(e, value) => {
            if (value === null) return;
            // free tasks have no assignee, so a picked employee would empty it
            updateFilter(
              value ? { withoutUsers: true, userIds: [] } : { withoutUsers: false },
            );
          }}
          sx={{
            '& button': { padding: '3px 8px', gap: '4px', fontSize: '12px' },
          }}
        >
          <ToggleButton value={true}>
            <NoAccountsIcon sx={{ fontSize: '16px' }} />
            {t('Unassigned')}
          </ToggleButton>
          <ToggleButton value={false}>
            <GroupsIcon sx={{ fontSize: '16px' }} />
            {t('All active')}
          </ToggleButton>
        </ToggleButtonGroup>

        <ProjectsMultiSelect
          projects={projects}
          selectedIds={filter.projectIds}
          onChange={(projectIds) => updateFilter({ projectIds })}
        />

        {/* free tasks have nobody to filter by, so it only shows next to them */}
        {manage && !filter.withoutUsers && (
          <EmployeesMultiSelect
            users={users}
            selectedIds={filter.userIds || []}
            onChange={(userIds) => updateFilter({ userIds })}
          />
        )}

        <SortSelect
          options={POOL_SORT_OPTIONS(t)}
          sortBy={filter.sortBy}
          sortOrder={filter.sortOrder || 'asc'}
          onChange={(sort) => updateFilter(sort)}
        />
      </Box>

      {/* only the two column layout scrolls the list, below it flows - flex-1
          keeps the pagination on screen instead of pushing it out */}
      <Box className="flex flex-col gap-2 lg:flex-1 lg:min-h-0 lg:overflow-y-auto pr-1">
        {isLoading ? (
          <ExoSkeleton n={4} size="92px" borderRadius="12px" />
        ) : tasks.length ? (
          <TaskCardList>
            {tasks.map((task) => (
              <PoolTaskCard
                key={task.id}
                task={task}
                manage={manage}
                users={users}
                isMember={(task.users || []).some(
                  (user) => user.id === userId,
                )}
                isAssigning={assigningIds.includes(task.id)}
                onTakeOver={handleTakeOver}
                onToggleUser={handleToggleUser}
              />
            ))}
          </TaskCardList>
        ) : (
          <Typography fontSize="13px" className="opacity-50 py-8 text-center">
            {t('The pool is empty')}
          </Typography>
        )}
      </Box>

      {/* the links carry first, last and every page in between, so more than
          three of them means there is something to turn to */}
      {apiData?.meta?.links?.length > 3 && (
        <Box
          className="backdrop-blur-lg rounded-lg p-1 shrink-0"
          sx={{ backgroundColor: colors.glass }}
        >
          <Pagination
            data={paginationData}
            setData={setApiData}
            setLoading={setIsPaginating}
            isSmall
          />
        </Box>
      )}
    </Box>
  );
};

export default TaskPool;
